import { CSRF_HEADER } from '@taim/shared';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export type Query = Record<string, string | number | boolean | Array<string | number> | null | undefined>;

export function buildUrl(path: string, query?: Query) {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    for (const v of Array.isArray(value) ? value : [value]) url.searchParams.append(key, String(v));
  }
  return url.toString();
}

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== 'GET') headers[CSRF_HEADER] = '1';
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['content-type'] = 'application/json';
  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: { error?: { code?: string; message?: string; details?: unknown } } | undefined;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const err = json?.error;
    throw new ApiRequestError(
      res.status,
      err?.code ?? `http_${res.status}`,
      err?.message ?? (text || res.statusText),
      err?.details,
    );
  }
  return json as T;
}

export const api = {
  get: <T = unknown>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T = unknown>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T = unknown>(path: string, body: unknown = {}) => request<T>('PUT', path, body),
  patch: <T = unknown>(path: string, body: unknown = {}) => request<T>('PATCH', path, body),
  delete: <T = unknown>(path: string) => request<T>('DELETE', path),
  /** multipart/form-data POST (file uploads); the browser sets the boundary. */
  upload: <T = unknown>(path: string, form: FormData) => request<T>('POST', path, form),
};
